import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import AddCoverImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { useMMDispatchActions, useMMSelector } from '../../../_hocs/withOwnReduxStore';
import DeviationDumbView from '../../../../DeviationViews/Dumbo';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import { getFolderNameValidationErrorMessage, MAX_TITLE_LENGTH } from '../../../../utils/isValidFolderName';
import { useGallections } from '../../../_hocs/withGallections';
import { setGallectionName, setGallectionDescription, setGallectionPrice } from '../../../actions';
import { useCurrentGallection } from '../../../_hocs/withCurrentGallection';
import { useCurrentSubfolder } from '../../../_hocs/withCurrentSubfolder';
import { getCanChangeGallectionDescription, getCanRenameGallection, getCanSetGallectionCover } from '../../../helpers';
import FolderRibbon from '../../../../Primitives/FolderRibbon';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import GallectionCoverSelector from '../../../_partials/GallectionCoverSelector';
import FolderCoverInfo from '../../../../FolderCoverInfo';
import { dollarsToPoints } from '../../../../utils/points';
import PriceField from './_partials/PriceField';
import useIsSellableAsPremiumGallery from '../../../../utils/hooks/useIsSellableAsPremiumGallery';
import { getGallectionById, getSubfoldersByParentId } from '../../../selectors';
import PremiumContent from './_partials/PremiumContent';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import GallectionEditor from '../../../../GallectionEditor';
import LegacyDescriptionEditor from '../../../../DescriptionEditor/lego/LegacyDescriptionEditor';
import gallectionSidebarStyles from './GallectionSidebar.scss';
export const GallectionSidebar = ()=>{
    var _currentGallection_premiumFolderData;
    const { t } = useTranslation();
    const { gallections } = useGallections();
    const { currentGallection: currentGallectionFolder } = useCurrentGallection();
    const { currentSubfolder } = useCurrentSubfolder();
    const currentGallection = currentSubfolder || currentGallectionFolder;
    const [isShowingMediaSelector, setIsShowingMediaSelector] = useState(false);
    const gallectionMM = useMMSelector((state)=>getGallectionById(state, currentGallection === null || currentGallection === void 0 ? void 0 : currentGallection.folderId));
    const subfoldersMM = useMMSelector((state)=>getSubfoldersByParentId(state, currentGallection === null || currentGallection === void 0 ? void 0 : currentGallection.folderId));
    const showSellAsPremiumGalleryButton = useIsSellableAsPremiumGallery(gallectionMM, subfoldersMM.length > 0);
    const showMediaSelector = useCallback(()=>setIsShowingMediaSelector(true), [
        setIsShowingMediaSelector
    ]);
    const hideMediaSelector = useCallback(()=>setIsShowingMediaSelector(false), [
        setIsShowingMediaSelector
    ]);
    const [nameErrorMessage, setNameErrorMessage] = useState();
    const actions = useMMDispatchActions({
        setGallectionName,
        setGallectionDescription,
        setGallectionPrice
    });
    const existingNames = useMemo(()=>gallections.filter((gallection)=>gallection.folderId !== currentGallection.folderId).map((gallection)=>gallection.name), [
        gallections,
        currentGallection.folderId
    ]);
    const onChangeGallectionName = useCallback((name)=>{
        const message = getFolderNameValidationErrorMessage(name, t, existingNames, currentGallection.type === 'gallery');
        setNameErrorMessage(message);
        actions.setGallectionName({
            folderId: currentGallection.folderId,
            name
        });
    }, [
        currentGallection.folderId,
        currentGallection.type,
        actions,
        existingNames,
        setNameErrorMessage,
        t
    ]);
    const editorApi = useRef(undefined);
    const onChangeGallectionDescriptionTiptap = useCallback(()=>{
        var _editorApi_current;
        // No way to not do this on every key...
        const description = JSON.stringify((_editorApi_current = editorApi.current) === null || _editorApi_current === void 0 ? void 0 : _editorApi_current.getStateForSave());
        actions.setGallectionDescription({
            folderId: currentGallection.folderId,
            description
        });
    }, [
        currentGallection.folderId,
        actions
    ]);
    const onChangeGallectionPrice = useCallback((value, error)=>{
        if (!value || !!error) {
            return;
        }
        actions.setGallectionPrice({
            folderId: currentGallection.folderId,
            pointsPrice: dollarsToPoints(+value)
        });
    }, [
        currentGallection.folderId,
        actions
    ]);
    const { isPaidFolder, isWatchToAccess } = normalizePremiumFolderData(currentGallection.premiumFolderData);
    const renderGallectionCoverBlock = ()=>{
        const gallectionThumb = currentGallection.thumb;
        const { isPremium: isThumbPremium } = normalizePremiumFolderData(gallectionThumb === null || gallectionThumb === void 0 ? void 0 : gallectionThumb.premiumFolderData);
        const canSetGallectionCover = getCanSetGallectionCover(currentGallection);
        return /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['cover-wrapper']
        }, gallectionThumb ? /*#__PURE__*/ React.createElement(DeviationDumbView, {
            useCoverMode: true,
            width: parseInt(gallectionSidebarStyles['width']),
            height: parseInt(gallectionSidebarStyles[isPaidFolder ? 'cover-height-paid-folder' : 'cover-height']),
            deviation: gallectionThumb
        }) : /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['no-cover']
        }), canSetGallectionCover && !gallectionThumb && /*#__PURE__*/ React.createElement("button", {
            className: gallectionSidebarStyles['cover-button-add'],
            onClick: showMediaSelector
        }, /*#__PURE__*/ React.createElement(AddCoverImageIcon, {
            className: gallectionSidebarStyles['cover-button-icon']
        }), /*#__PURE__*/ React.createElement("br", null), t('mediapopup.setGallectionCover')), canSetGallectionCover && gallectionThumb && /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['cover-buttons-wrapper']
        }, isThumbPremium && /*#__PURE__*/ React.createElement(FolderCoverInfo, null), /*#__PURE__*/ React.createElement(IconButtonWithBackground, {
            size: "small",
            icon: EditIcon,
            className: gallectionSidebarStyles['cover-button-edit'],
            onClick: showMediaSelector
        })));
    };
    const renderSubscribersCount = ()=>{
        var _currentGallection_premiumFolderData, _currentGallection_premiumFolderData1;
        return /*#__PURE__*/ React.createElement("div", {
            className: gallectionSidebarStyles['subscribers-counter']
        }, /*#__PURE__*/ React.createElement("span", null, (_currentGallection_premiumFolderData = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData === void 0 ? void 0 : _currentGallection_premiumFolderData.numSubscribers), ' ', t('common.supporters', {
            count: (_currentGallection_premiumFolderData1 = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData1 === void 0 ? void 0 : _currentGallection_premiumFolderData1.numSubscribers
        }));
    };
    const renderGallectionNameInput = ()=>/*#__PURE__*/ React.createElement(TextInput, {
            id: "gallection-name",
            autoComplete: "off",
            validationResult: nameErrorMessage ? 'error' : undefined,
            details: nameErrorMessage,
            wrapperClassName: c(gallectionSidebarStyles['gallection-name'], showSellAsPremiumGalleryButton && gallectionSidebarStyles['gallection-name_gallery_thumb_premium_flow']),
            label: t('mediapopup.gallections.input.name'),
            value: currentGallection.name,
            onChange: (ev)=>onChangeGallectionName(ev.target.value),
            disabled: !getCanRenameGallection(currentGallection),
            maxLength: MAX_TITLE_LENGTH
        });
    const [descriptionValue, setDescriptionValue] = useState(currentGallection.richDescription);
    const renderDescriptionTextarea = ()=>{
        var _descriptionValue_html;
        let descriptionPlaceholder;
        if (isPaidFolder) {
            descriptionPlaceholder = t('mediapopup.gallections.gallery.paid.description.placeholder');
        } else if (isWatchToAccess) {
            descriptionPlaceholder = t('mediapopup.gallections.gallery.watchers.description.placeholder');
        }
        const isLegacyDescription = typeof descriptionValue === 'string' && descriptionValue !== '' || (descriptionValue === null || descriptionValue === void 0 ? void 0 : (_descriptionValue_html = descriptionValue.html) === null || _descriptionValue_html === void 0 ? void 0 : _descriptionValue_html.type) === 'writer';
        return /*#__PURE__*/ React.createElement(FormElement, {
            inputId: 'description',
            label: /*#__PURE__*/ React.createElement(InputLabelText, null, t('mediapopup.gallections.input.description'))
        }, isLegacyDescription && /*#__PURE__*/ React.createElement(LegacyDescriptionEditor, {
            descriptionHtml: isPapiEditorText(descriptionValue) ? descriptionValue.html : {
                features: '',
                type: 'writer',
                markup: descriptionValue
            },
            onConvertToDraft: (v)=>{
                var _editorApi_current;
                editorApi === null || editorApi === void 0 ? void 0 : (_editorApi_current = editorApi.current) === null || _editorApi_current === void 0 ? void 0 : _editorApi_current.setContent(v);
                setDescriptionValue(v);
            }
        }), !isLegacyDescription && /*#__PURE__*/ React.createElement(GallectionEditor, {
            initialContent: descriptionValue,
            onInitialContentReady: (api)=>editorApi.current = api,
            onChange: onChangeGallectionDescriptionTiptap,
            placeholder: descriptionPlaceholder
        }));
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: c(gallectionSidebarStyles['root'], showSellAsPremiumGalleryButton && gallectionSidebarStyles['gallery_thumb_premium_flow'])
    }, isShowingMediaSelector && /*#__PURE__*/ React.createElement(GallectionCoverSelector, {
        gallection: currentGallection,
        onClose: hideMediaSelector
    }), /*#__PURE__*/ React.createElement(FolderRibbon, {
        premiumFolderData: currentGallection.premiumFolderData,
        className: gallectionSidebarStyles['premium-badge']
    }), /*#__PURE__*/ React.createElement("div", {
        className: gallectionSidebarStyles['sidebar']
    }, renderGallectionCoverBlock(), isPaidFolder && renderSubscribersCount(), /*#__PURE__*/ React.createElement("div", {
        className: gallectionSidebarStyles['form']
    }, showSellAsPremiumGalleryButton && /*#__PURE__*/ React.createElement(PremiumContent, null), ((_currentGallection_premiumFolderData = currentGallection.premiumFolderData) === null || _currentGallection_premiumFolderData === void 0 ? void 0 : _currentGallection_premiumFolderData.type) === 'paid' && /*#__PURE__*/ React.createElement(PriceField, {
        onChange: onChangeGallectionPrice,
        currentGallection: currentGallection
    }), renderGallectionNameInput(), !currentSubfolder && getCanChangeGallectionDescription(currentGallection) && renderDescriptionTextarea())));
};
GallectionSidebar.displayName = 'GallectionSidebar';
function isPapiEditorText(value) {
    return value && value.html;
}
export default GallectionSidebar;

//# sourceMappingURL=GallectionSidebar.js.map